import { useEffect, useState } from "react";
import { ChatDetailMessage } from "../types/sidekick";
import { useDispatch, useSelector } from "react-redux";
import { fetchChatSessionDetails, selectChatDetail } from "../features/sidekick/sidekickSlice";
import { useDataPagination, UseDataPaginationResult } from "./useDataPagination";

export interface UseChatSessionResult extends UseDataPaginationResult<ChatDetailMessage> {
    chatSessionData: ChatDetailMessage[];
    conversationId?: string;
    conversationSubject?: string;
    scrollKey: number;
}

export const useChatSessionDetails = (
    conversationId?: string,
    initialPage: number = 1,
    initialPageSize: number = 10
): UseChatSessionResult => {
    const dispatch = useDispatch();
    const chatDetailState = useSelector(selectChatDetail);
    const [scrollKey, setScrollKey] = useState(0);

    // Use the base pagination hook
    const paginationProps = useDataPagination({
        data: chatDetailState.data,
        totalCount: chatDetailState.totalCount,
        loading: chatDetailState.loading,
        error: chatDetailState.error,
        currentPage: chatDetailState.page,
        rowsPerPage: chatDetailState.pageSize,
        onPageChange: (page) => {
            setScrollKey(k => k + 1);
            if (conversationId) {
                dispatch(fetchChatSessionDetails({
                    conversationId,
                    page,
                    pageSize: chatDetailState.pageSize,
                    requestSource: 'pagination'
                }));
            }
        },
        onRowsPerPageChange: (pageSize) => {
            if (conversationId) {
                dispatch(fetchChatSessionDetails({
                    conversationId,
                    page: 1, // Reset to first page
                    pageSize,
                    requestSource: 'pagination'
                }));
            }
        }
    });

    // Fetch chat session details when conversationId changes
    useEffect(() => {
        if (conversationId && !chatDetailState.loading && !chatDetailState.error) {
            if (!chatDetailState.conversationId || chatDetailState.conversationId !== conversationId) {
                dispatch(fetchChatSessionDetails({
                    conversationId,
                    page: chatDetailState.page || 1,
                    pageSize: chatDetailState.pageSize || 10,
                    requestSource: 'initial'
                }));
            }
        }
    }, [
        dispatch,
        conversationId,
        chatDetailState.loading,
        chatDetailState.error,
        chatDetailState.conversationId,
    ]);

    return {
        ...paginationProps,
        chatSessionData: chatDetailState.data,
        conversationId: chatDetailState.conversationId || conversationId,
        conversationSubject: chatDetailState.conversationSubject,
        scrollKey
    };
};